import { Action } from 'redux';
import { Dispatch } from 'react-redux';
import { PageViewModel, AppDispatcher } from '@models/common.models';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType } from '@common/components';
import * as React from 'react';
import { ToastOptions } from '@common/components/toast/toast';
import { DocumentHistoryViewModel } from '@models/DocumentHistory';
import { documentService } from '@services/DocumentService';
import { AppState } from 'src';
import { requestAction } from '@common/components/common.actions';

export enum FailedEmailActionType {
  SearchFailedEmailListStart = 'SearchFailedEmailListStart',
  SearchFailedEmailListSuccess = 'SearchFailedEmailListSuccess',
  SearchFailedEmailListFailure = 'SearchFailedEmailListFailure',
  SetFailedEmailLoading = 'SetFailedEmailLoading',
  SetFailedEmailList = 'SetFailedEmailList',
  SetOwnership = 'SetOwnership'
}

/***** Search Failed Email List *****/
export interface SearchFailedEmailListStartAction extends Action {
  readonly type: FailedEmailActionType.SearchFailedEmailListStart;
  readonly query?: string;
}
export interface SearchFailedEmailListSuccessAction extends Action {
  readonly type: FailedEmailActionType.SearchFailedEmailListSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<DocumentHistoryViewModel>;
}

export interface SearchFailedEmailListFailureAction extends Action {
  readonly type: FailedEmailActionType.SearchFailedEmailListFailure;
  readonly error: string;
}

/***** Search Failed Email List *****/
export function searchFailedEmailList(
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string,
  query?: string
) {
  return async (dispatch: Dispatch<DocumentHistoryViewModel>) => {
    dispatch(searchFailedEmailListStart(query));
    try {
      const result: PageViewModel<DocumentHistoryViewModel> = await documentService.getFailedEmails(
        pageNumber,
        pageSize,
        orderBy,
        orderDirection,
        query
      );
      dispatch(searchFailedEmailListSuccess(pageNumber, pageSize, orderBy, result!));
    } catch (e) {
      dispatch(searchFailedEmailListFailure(e.toString()));
    }
  };
}
export function searchFailedEmailListStart(query?: string): SearchFailedEmailListStartAction {
  return {
    type: FailedEmailActionType.SearchFailedEmailListStart,
    query: query
  };
}
export function searchFailedEmailListSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<DocumentHistoryViewModel>
): SearchFailedEmailListSuccessAction {
  return {
    type: FailedEmailActionType.SearchFailedEmailListSuccess,
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result
  };
}
export function searchFailedEmailListFailure(error: string): SearchFailedEmailListFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);
  return {
    type: FailedEmailActionType.SearchFailedEmailListFailure,
    error: error
  };
}

export interface SetFailedEmailLoadingAction extends Action {
  readonly type: FailedEmailActionType.SetFailedEmailLoading;
  readonly loading: boolean;
}
export function setFailedEmailLoading(isLoading: boolean): SetFailedEmailLoadingAction {
  return {
    type: FailedEmailActionType.SetFailedEmailLoading,
    loading: isLoading
  };
}

export interface SetFailedEmailListAction extends Action {
  readonly type: FailedEmailActionType.SetFailedEmailList;
  readonly query?: string;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly results: Array<DocumentHistoryViewModel>;
  readonly totalResults: number;
}
export function setFailedEmailList(searchViewModel: {
  page: number;
  pageSize: number;
  orderBy: string;
  results: Array<DocumentHistoryViewModel>;
  query?: string;
  totalResults: number;
}): SetFailedEmailListAction {
  return {
    type: FailedEmailActionType.SetFailedEmailList,
    query: searchViewModel.query,
    pageNumber: searchViewModel.page,
    pageSize: searchViewModel.pageSize,
    orderBy: searchViewModel.orderBy,
    results: searchViewModel.results,
    totalResults: searchViewModel.totalResults
  };
}
export function getFailedEmailList() {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    let stateFailedEmail = getState().failedEmailState;
    if (!stateFailedEmail) {
      return;
    }
    dispatch(
      requestAction(
        () =>
          documentService.getFailedEmails(
            stateFailedEmail.pageNumber,
            stateFailedEmail.pageSize,
            stateFailedEmail.orderBy,
            'desc',
            stateFailedEmail.query
          ),
        setFailedEmailList,
        {
          hasToast: true,
          toastSuccessMessage: 'Refreshed Successfully.'
        }
      )
    );
  };
}

export interface SetOwnershipAction extends Action {
  readonly type: FailedEmailActionType.SetOwnership;
  readonly emailHistory: DocumentHistoryViewModel;
}
export function setOwnership(emailHistory: DocumentHistoryViewModel): SetOwnershipAction {
  return {
    type: FailedEmailActionType.SetOwnership,
    emailHistory: emailHistory
  };
}
export function changeFailedEmailOwnership(emailHistoryId: number, release: boolean) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    await dispatch(
      requestAction(
        () =>
          release ? documentService.releaseFailedEmail(emailHistoryId) : documentService.ownFailedEmail(emailHistoryId),
        () => {
          return { type: '' };
        },
        { hasToast: true, toastSuccessMessage: 'Ownership Updated Successfully.' }
      )
    );
  };
}

export function removeFailedEmail(emailHistoryId: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    await dispatch(
      requestAction(
        () => documentService.removeFailedEmail(emailHistoryId),
        () => {
          return { type: '' };
        },
        { hasToast: true, toastSuccessMessage: 'Removed Successfully.' }
      )
    );
  };
}



// WEBPACK FOOTER //
// ./src/failed-email/failed-email.actions.tsx